import { SeoPageConfig } from "./seo-pages";

/**
 * Посадочные под запросы про формат результата, а не про исходник.
 * Все описанные возможности реально реализованы: экспорт TXT/DOCX/PDF/SRT
 * (см. shared/helper_lib/transcription-helpers.tsx), таймкоды, спикеры и AI-сводка.
 */
export const EXPORT_PAGES: SeoPageConfig[] = [
  {
    path: "/audio-to-word",
    cardTitle: "Аудио в Word (DOCX)",
    metaTitle: "Перевод аудио в Word онлайн | Расшифровка аудио в DOCX",
    metaDescription:
      "Расшифровка аудио и видео сразу в файл Word. Готовый DOCX с абзацами, таймкодами и разделением по спикерам — остаётся только вычитать.",
    customTitle: "Перевод аудио в документ Word",
    customSubtitle: "Готовый DOCX вместо простыни текста — с абзацами, спикерами и таймкодами.",
    seoDescription: (
      <>
        <p>
          Расшифровка чаще всего нужна не сама по себе, а как документ, который потом правят и пересылают. Поэтому
          результат выгружается прямо в DOCX: файл открывается в Word, «Google Документах» или любом совместимом
          редакторе и сразу готов к правке.
        </p>
        <p>
          В документе сохраняется структура — реплики разделены по говорящим, к ним привязаны таймкоды, текст разбит на
          абзацы, а не идёт сплошным потоком. Это принципиальная разница с простым TXT: часовую расшифровку одним
          сплошным блоком вычитывать почти невозможно.
        </p>
        <p>
          Если полный текст не нужен, рядом доступна AI-сводка: краткое изложение и ключевые моменты, из которых
          удобно собрать протокол или конспект.
        </p>
      </>
    ),
    faq: [
      {
        question: "Сохраняются ли в документе спикеры и таймкоды?",
        answer:
          "Да. Реплики сгруппированы по говорящим, к каждой привязано время в записи, текст разбит на абзацы."
      },
      {
        question: "В каких редакторах откроется файл?",
        answer:
          "DOCX — стандартный формат: Microsoft Word, «Google Документы», LibreOffice, «Р7-Офис» и «МойОфис» открывают его без конвертации."
      },
      {
        question: "Какие ещё форматы доступны?",
        answer: "Кроме DOCX — TXT, PDF и SRT. Формат выбирается уже после расшифровки, переделывать ничего не нужно."
      },
      {
        question: "Можно ли получить не весь текст, а только суть?",
        answer:
          "Да, для этого есть AI-сводка: она собирает краткое изложение и список ключевых моментов вместо полной расшифровки."
      }
    ]
  },
  {
    path: "/audio-to-pdf",
    cardTitle: "Аудио в PDF",
    metaTitle: "Расшифровка аудио в PDF онлайн | Перевод записи в PDF-документ",
    metaDescription:
      "Превратите аудио или видео в PDF-документ с расшифровкой. Готовый файл с таймкодами и спикерами, который не изменится при пересылке.",
    customTitle: "Расшифровка аудио в PDF",
    customSubtitle: "Документ, который одинаково выглядит у всех, кому вы его отправите.",
    seoDescription: (
      <>
        <p>
          PDF имеет смысл выбирать тогда, когда расшифровку не редактируют, а передают: прикладывают к отчёту,
          отправляют заказчику или сохраняют в архив. Формат фиксирует вёрстку — документ выглядит одинаково независимо
          от того, чем его открыли.
        </p>
        <p>
          Внутри та же структура, что и в остальных форматах: реплики разделены по спикерам, к ним привязаны таймкоды,
          текст разбит на абзацы. Разница только в том, что PDF не предполагает правки — для неё лучше подходит DOCX.
        </p>
        <p>
          Выбирать формат заранее не нужно: расшифровка делается один раз, а выгрузить её можно в любой из доступных
          форматов, в том числе в несколько сразу.
        </p>
      </>
    ),
    faq: [
      {
        question: "Когда лучше PDF, а когда Word?",
        answer:
          "PDF — если документ нужно передать или заархивировать без изменений. DOCX — если расшифровку будут вычитывать и править."
      },
      {
        question: "Можно ли выгрузить в оба формата сразу?",
        answer: "Да. Расшифровка делается один раз, а скачать её можно в любом из форматов и в любом количестве."
      },
      {
        question: "Есть ли в PDF таймкоды?",
        answer: "Да, время привязано к репликам так же, как в остальных форматах выгрузки."
      },
      {
        question: "Можно ли искать по тексту внутри PDF?",
        answer: "Да, текст в документе обычный, а не картинка — поиск и копирование работают."
      }
    ]
  },
  {
    path: "/transcription-with-timestamps",
    cardTitle: "Расшифровка с таймкодами",
    relatedArticles: ["tochnost-rasshifrovki-99-procentov"],
    metaTitle: "Расшифровка аудио с таймкодами онлайн | Транскрибация с тайм-кодами",
    metaDescription:
      "Транскрибация аудио и видео с таймкодами: каждая реплика привязана ко времени в записи. Удобно для цитирования, монтажа и навигации.",
    customTitle: "Расшифровка с таймкодами",
    customSubtitle: "Каждая реплика привязана ко времени — нужный фрагмент находится за секунды.",
    seoDescription: (
      <>
        <p>
          Таймкод превращает расшифровку из просто текста в навигацию по записи. Нашли спорную фразу — сразу видите,
          на какой минуте её послушать, и не перематываете файл наугад. Для длинных записей это экономит больше
          времени, чем сама расшифровка.
        </p>
        <p>
          Пригождается это в трёх ситуациях чаще всего: при цитировании, когда нужно сослаться на конкретный момент;
          при монтаже, когда по тексту ищут места для склейки; и при проверке, когда важно убедиться, что модель верно
          распознала ключевую фразу.
        </p>
        <p>
          Таймкоды проставляются автоматически для всех записей и сохраняются во всех форматах выгрузки. В SRT они
          превращаются в готовые тайминги субтитров.
        </p>
      </>
    ),
    faq: [
      {
        question: "Нужно ли включать таймкоды отдельно?",
        answer: "Нет, они проставляются автоматически для всех расшифровок и сохраняются при выгрузке."
      },
      {
        question: "Насколько точна привязка ко времени?",
        answer:
          "Привязка идёт на уровне реплик и обычно расходится с записью не больше чем на доли секунды — этого достаточно и для навигации, и для субтитров."
      },
      {
        question: "Сохраняются ли таймкоды в Word и PDF?",
        answer: "Да, во всех форматах выгрузки. В SRT они становятся таймингами субтитров."
      },
      {
        question: "Можно ли получить текст без таймкодов?",
        answer: "Да, выгрузка в TXT даёт чистый текст, если время в документе не нужно."
      }
    ]
  },
  {
    path: "/speaker-diarization",
    cardTitle: "Разделение по спикерам",
    relatedArticles: ["tochnost-rasshifrovki-99-procentov"],
    metaTitle: "Расшифровка с разделением по спикерам | Транскрибация диалогов",
    metaDescription:
      "Транскрибация с разделением по спикерам: реплики сгруппированы по говорящим. Для интервью, созвонов, круглых столов и фокус-групп.",
    customTitle: "Расшифровка с разделением по спикерам",
    customSubtitle: "Реплики разложены по говорящим — видно, кто что сказал.",
    seoDescription: (
      <>
        <p>
          Когда в записи больше одного человека, сплошной текст почти бесполезен: непонятно, где заканчивается вопрос и
          начинается ответ. Разделение по спикерам решает эту проблему — модель различает голоса и группирует реплики
          по говорящим.
        </p>
        <p>
          Имён сервис, разумеется, не знает: участники обозначаются как «Спикер 1», «Спикер 2» и так далее, а подписать
          их можно вручную при вычитке. Зато порядок реплик и границы между ними определяются автоматически, и это
          самая трудоёмкая часть ручной расшифровки.
        </p>
        <p>
          Точность зависит от того, насколько различимы голоса и как участники записаны. Отдельные микрофоны у каждого
          дают лучший результат; несколько человек за одним микрофоном в переговорке — самый сложный случай, как и
          моменты, где говорят одновременно.
        </p>
      </>
    ),
    faq: [
      {
        question: "Определяет ли сервис имена участников?",
        answer:
          "Нет, говорящие обозначаются как «Спикер 1», «Спикер 2» и так далее. Имена проставляются вручную при вычитке."
      },
      {
        question: "Сколько спикеров различает модель?",
        answer:
          "Ограничения по числу участников нет, но чем их больше и чем ближе тембры голосов, тем выше вероятность спутать реплики."
      },
      {
        question: "Что происходит, когда говорят одновременно?",
        answer:
          "Это самый сложный случай: в местах наложения речи возможны ошибки в распределении реплик. Таймкоды помогают быстро проверить такие фрагменты."
      },
      {
        question: "Нужно ли включать разделение отдельно?",
        answer: "Нет, оно работает автоматически и сохраняется во всех форматах выгрузки."
      }
    ]
  },
  {
    path: "/audio-summary",
    cardTitle: "Краткое содержание записи",
    relatedArticles: ["tochnost-rasshifrovki-99-procentov"],
    metaTitle: "Краткое содержание аудио и видео | AI-саммари записи онлайн",
    metaDescription:
      "Краткое содержание записи вместо полной расшифровки: главное и ключевые моменты часовой встречи или лекции за несколько минут.",
    customTitle: "Краткое содержание записи",
    customSubtitle: "Главное из часовой записи вместо десяти страниц текста.",
    seoDescription: (
      <>
        <p>
          Полная расшифровка часовой встречи — это примерно восемь-десять страниц текста, и читать их целиком обычно
          некому. AI-сводка решает обратную задачу: собирает из расшифровки краткое изложение и список ключевых
          моментов, по которым видно, о чём был разговор и к чему пришли.
        </p>
        <p>
          Полный текст при этом никуда не девается — он остаётся рядом со сводкой. Это важно, потому что сводка
          пересказывает, а не цитирует: если нужна точная формулировка, её берут из расшифровки, а таймкод подсказывает,
          где переслушать оригинал.
        </p>
        <p>
          Чаще всего так делают протоколы совещаний, конспекты лекций и выжимки из длинных интервью — то есть там, где
          важна суть, а дословность нужна лишь местами.
        </p>
      </>
    ),
    faq: [
      {
        question: "Заменяет ли сводка полную расшифровку?",
        answer:
          "Нет, она идёт вместе с ней. Сводка пересказывает содержание, а точные формулировки всегда можно взять из полного текста."
      },
      {
        question: "Что входит в сводку?",
        answer: "Краткое изложение записи и список ключевых моментов — того, что стоит запомнить или вынести в протокол."
      },
      {
        question: "Работает ли это для записей не на русском?",
        answer: "Да, сводка формируется и для других языков, поддерживаемых сервисом."
      },
      {
        question: "Можно ли доверять сводке без проверки?",
        answer:
          "Как и любой пересказ, она может сместить акценты. Для протокола и решений формулировки стоит сверить с полным текстом."
      }
    ]
  }
];
